import { VALIDATION_MESSAGES, DISALLOWED_LOCATION_CHARS } from '@/src/data'

type ValidationResult = {
  isValid: boolean
  errorMessage?: string
}

export const validateLocationInput = (
  input: string,
  skipFormatValidation = false
): ValidationResult => {
  if (!input.trim()) {
    return {
      isValid: true,
      errorMessage: undefined,
    }
  }

  // Check for invalid separators (periods, semicolons, slashes)
  if (/[.;/]/.test(input)) {
    return {
      isValid: false,
      errorMessage: VALIDATION_MESSAGES.LOCATION.INVALID_FORMAT,
    }
  }

  // Check for invalid characters in each part
  const hasInvalidChars = input
    .split(',')
    .some((part) => DISALLOWED_LOCATION_CHARS.test(part))

  if (hasInvalidChars) {
    return {
      isValid: false,
      errorMessage: VALIDATION_MESSAGES.LOCATION.INVALID_CHARS,
    }
  }

  if (skipFormatValidation) {
    return {
      isValid: true,
      errorMessage: undefined,
    }
  }

  const sanitizedInput = input.replace(/'/g, ' ')
  const parts = sanitizedInput
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0)

  // Handle standalone ZIP code
  if (parts.length === 1) {
    const singlePart = parts[0]
    if (/^\d{5}(?:-\d{4})?$/.test(singlePart)) {
      return {
        isValid: true,
        errorMessage: undefined,
      }
    }
  }

  // Handle state and zip combination
  if (parts.length === 2) {
    const [firstPart, secondPart] = parts

    // Check if it's a state + zip combination
    const isState = /^[A-Za-z]{2}$/i.test(firstPart)
    const isZip = /^\d{5}(?:-\d{4})?$/.test(secondPart)
    if (isState && isZip) {
      return {
        isValid: true,
        errorMessage: undefined,
      }
    }

    // Check if it's a city + state combination
    const isValidCity = /^[A-Za-z\s]+$/i.test(firstPart)
    const isValidState = /^[A-Za-z]{2}$/i.test(secondPart)
    if (isValidCity && isValidState) {
      return {
        isValid: true,
        errorMessage: undefined,
      }
    }
  }

  // Handle city, state, zip combination
  if (parts.length === 3) {
    const [cityPart, statePart, zipPart] = parts
    const isValidCity = /^[A-Za-z\s]+$/i.test(cityPart)
    const isValidState = /^[A-Za-z]{2}$/i.test(statePart)
    const isValidZip = /^\d{5}(?:-\d{4})?$/.test(zipPart)

    if (isValidCity && isValidState && isValidZip) {
      return {
        isValid: true,
        errorMessage: undefined,
      }
    }
  }

  // If no comma is present in a multi-word input
  if (!input.includes(',') && input.split(/\s+/).length > 1) {
    const lastWord = input.split(/\s+/).pop() || ''
    if (/^[A-Za-z]{2}$/i.test(lastWord)) {
      return {
        isValid: false,
        errorMessage: VALIDATION_MESSAGES.LOCATION.INVALID_FORMAT,
      }
    }
  }

  return {
    isValid: false,
    errorMessage: VALIDATION_MESSAGES.LOCATION.INVALID_FORMAT,
  }
}
